import type {
  TokenResponse,
  LoginRequest,
  RegisterRequest,
  Customer,
  SubscriptionStatus,
  Preferences,
  PreferencesRequest,
  Newsletter,
  GenerateNewsletterRequest,
  CanGenerateResponse,
  UploadedFile,
  PresetsResponse,
  Preset,
  TiersResponse,
  Tier,
  ModelsListResponse,
  ModelTierInfoResponse,
  FeedbackRequest,
  Feedback,
  ContactFormRequest,
  ContactFormResponse,
  TaskStatus,
  ApiError,
  S3ReportsResponse,
  ActiveTasksResponse,
  AdminErrorsResponse,
  AdminErrorDetail,
  AdminErrorSummary,
  ErrorSeverity,
  ErrorCategory,
  ActiveGenerationsResponse,
  DeliverySchedule,
  DeliveryScheduleRequest,
  DeliverySchedulesResponse,
  UserSpendingResponse,
  RecentGenerationsResponse,
  GenerationDetail,
  AnalyticsResponse,
  ComparisonResponse,
  CacheStatsResponse,
  CacheCleanupResponse,
  FreeReportsResponse,
  ResearchHistoryResponse,
  ResearchStats,
  TopicNoveltyResponse,
  SimilarResearchResponse,
  FreeReportRequest,
  FreeReportResponse,
  FreeReportStatusResponse,
  FingerprintCheckRequest,
  FingerprintCheckResponse,
  VerifyEmailRequest,
  ResendVerificationRequest,
  EmailVerificationStatusResponse,
  CreateInfluencerRequest,
  CreateInfluencerResponse,
  InfluencerAccountsResponse,
  VerdictAdminStatsResponse,
  VerdictActiveLogicTreesResponse,
  VerdictFingerprintsResponse,
  VerdictResearchSessionsResponse,
  VerdictCostTrackingResponse,
} from '@/types';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL || '';

class ApiClient {
  private getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        detail: 'An error occurred',
      }));

      // Handle expired or invalid token - clear token and redirect to login
      // Only redirect if:
      // 1. There was actually a token (user was supposedly logged in)
      // 2. Not already on login/register pages
      // 3. Not on the landing page (which should work without auth)
      if (response.status === 401 && token) {
        localStorage.removeItem('access_token');
        const currentPath = window.location.pathname;
        // Don't redirect from login, register, or landing page
        if (!currentPath.includes('/login') &&
            !currentPath.includes('/register') &&
            currentPath !== '/') {
          window.location.href = '/login?session_expired=true';
          return new Promise(() => {}); // Never resolves - page will redirect
        }
      }

      // Extract error message - handle both string and object detail formats
      let errorMessage: string;
      let errorCode: string | undefined;
      if (typeof error.detail === 'string') {
        errorMessage = error.detail;
      } else if (error.detail && typeof error.detail === 'object') {
        // Handle structured error responses (e.g., { error: "code", message: "..." })
        const detailObj = error.detail as { message?: string; error?: string; code?: string };
        errorMessage = detailObj.message || detailObj.error || `HTTP ${response.status}`;
        errorCode = detailObj.code;
      } else {
        errorMessage = `HTTP ${response.status}`;
      }

      const err = new Error(errorMessage);
      if (errorCode) {
        (err as Error & { code?: string }).code = errorCode;
      }
      throw err;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Auth endpoints
  async register(data: RegisterRequest): Promise<TokenResponse> {
    return this.request<TokenResponse>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(data: LoginRequest): Promise<TokenResponse> {
    return this.request<TokenResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async googleAuth(idToken: string): Promise<TokenResponse> {
    return this.request<TokenResponse>('/api/v1/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    });
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, new_password: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    });
  }

  async getCurrentUser(): Promise<Customer> {
    return this.request<Customer>('/api/v1/auth/me');
  }

  async completeOnboarding(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/api/v1/auth/onboarding/complete', {
      method: 'POST',
    });
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendVerificationEmail(data: ResendVerificationRequest): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/api/v1/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVerificationStatus(): Promise<EmailVerificationStatusResponse> {
    return this.request<EmailVerificationStatusResponse>('/api/v1/auth/verification-status');
  }

  // Profile endpoints
  async updateProfile(data: { first_name?: string; last_name?: string }): Promise<void> {
    return this.request('/api/v1/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: { current_password: string; new_password: string }): Promise<void> {
    return this.request('/api/v1/me/password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAccount(data: { password: string; confirmation: string }): Promise<{ message: string; email: string }> {
    return this.request('/api/v1/me', {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  // Preferences endpoints
  async getPreferences(): Promise<Preferences> {
    return this.request<Preferences>('/api/v1/me/preferences');
  }

  async savePreferences(data: PreferencesRequest): Promise<Preferences> {
    return this.request<Preferences>('/api/v1/me/preferences', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * @deprecated Use addDeliverySchedule/updateDeliverySchedule instead.
   * This updates the old single-schedule system in customer_preferences.
   * The new delivery_schedules table supports multiple schedules per day.
   */
  async updateDeliveryTime(time: string, timezone?: string): Promise<void> {
    const params = new URLSearchParams({ delivery_time: time });
    if (timezone) params.append('timezone', timezone);
    return this.request(`/api/v1/me/preferences/delivery-time?${params}`, {
      method: 'PATCH',
    });
  }

  async toggleNewsletterStatus(isActive: boolean): Promise<void> {
    return this.request(`/api/v1/me/preferences/toggle?is_active=${isActive}`, {
      method: 'PATCH',
    });
  }

  // Delivery Schedules endpoints (multiple newsletters per day)
  async getDeliverySchedules(): Promise<DeliverySchedulesResponse> {
    return this.request<DeliverySchedulesResponse>('/api/v1/me/schedules');
  }

  async addDeliverySchedule(data: DeliveryScheduleRequest): Promise<DeliverySchedule> {
    return this.request<DeliverySchedule>('/api/v1/me/schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDeliverySchedule(scheduleId: string, data: DeliveryScheduleRequest): Promise<DeliverySchedule> {
    return this.request<DeliverySchedule>(`/api/v1/me/schedules/${scheduleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDeliverySchedule(scheduleId: string): Promise<void> {
    return this.request(`/api/v1/me/schedules/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  // Subscription endpoints
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    return this.request<SubscriptionStatus>('/api/v1/newsletter/subscription');
  }

  // Newsletter endpoints
  async getNewsletterHistory(options: { limit?: number } | number = 10): Promise<Newsletter[]> {
    const limit = typeof options === 'number' ? options : options.limit || 10;
    return this.request<Newsletter[]>(`/api/v1/newsletter/history?limit=${limit}`);
  }

  async getNewsletterDetail(newsletterId: string): Promise<Newsletter & { content?: string }> {
    return this.request<Newsletter & { content?: string }>(`/api/v1/newsletter/${newsletterId}`);
  }

  async generateNewsletter(data: GenerateNewsletterRequest): Promise<{ task_id: string }> {
    return this.request('/api/v1/newsletter/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async canGenerateNewsletter(): Promise<CanGenerateResponse> {
    return this.request<CanGenerateResponse>('/api/v1/newsletter/can-generate');
  }

  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    return this.request<TaskStatus>(`/api/v1/newsletter/task/${taskId}`);
  }

  async getActiveNewsletters(): Promise<ActiveTasksResponse> {
    return this.request<ActiveTasksResponse>('/api/v1/newsletter/active');
  }

  // S3 Report endpoints (for master users)
  async getS3Reports(): Promise<S3ReportsResponse> {
    return this.request<S3ReportsResponse>('/api/v1/newsletter/s3-reports');
  }

  async updateNewsletterS3Url(newsletterId: string, s3Url: string): Promise<{ success: boolean; message: string; s3_url: string }> {
    return this.request(`/api/v1/newsletter/${newsletterId}/s3-url?s3_url=${encodeURIComponent(s3Url)}`, {
      method: 'PATCH',
    });
  }

  // File endpoints
  async uploadFile(file: File): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();

    const response = await fetch(`${API_BASE}/api/v1/files/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    // Handle expired or invalid token - clear token and redirect to login
    if (response.status === 401) {
      localStorage.removeItem('access_token');
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
        window.location.href = '/login?session_expired=true';
        return new Promise(() => {}); // Never resolves - page will redirect
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail);
    }
    return response.json();
  }

  async getFiles(): Promise<UploadedFile[]> {
    return this.request<UploadedFile[]>('/api/v1/files');
  }

  async deleteFile(fileId: string): Promise<void> {
    return this.request(`/api/v1/files/${fileId}`, { method: 'DELETE' });
  }

  // Session file endpoints
  async uploadSessionFile(sessionId: string, file: File): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();

    const response = await fetch(`${API_BASE}/api/v1/research/sessions/${sessionId}/files`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('access_token');
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
        window.location.href = '/login';
      }
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload session file');
    }

    return response.json();
  }

  async getSessionFiles(sessionId: string): Promise<UploadedFile[]> {
    return this.request<UploadedFile[]>(`/api/v1/research/sessions/${sessionId}/files`);
  }

  async deleteSessionFile(sessionId: string, fileId: string): Promise<void> {
    return this.request(`/api/v1/research/sessions/${sessionId}/files/${fileId}`, { method: 'DELETE' });
  }

  // Presets endpoints
  async getPresets(category?: string): Promise<PresetsResponse> {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    return this.request<PresetsResponse>(`/api/v1/prompts/presets${params}`);
  }

  async getPreset(presetId: string): Promise<Preset> {
    return this.request<Preset>(`/api/v1/prompts/presets/${presetId}`);
  }

  // Tiers endpoints
  async getTiers(includeStripeIds: boolean = false): Promise<TiersResponse> {
    const params = includeStripeIds ? '?include_stripe_ids=true' : '';
    return this.request<TiersResponse>(`/api/v1/tiers${params}`);
  }

  async getTier(tierName: string): Promise<{ tier: Tier; beta_phase: boolean }> {
    return this.request(`/api/v1/tiers/${tierName}`);
  }

  async getMyTier(): Promise<{ current_tier: Tier; upgrade_recommendation: unknown; beta_phase: boolean }> {
    return this.request('/api/v1/me/tier');
  }

  // Models endpoint (for verdict/council components)
  async getModels(tier?: string): Promise<ModelsListResponse> {
    const params = tier ? `?tier=${encodeURIComponent(tier)}` : '';
    return this.request<ModelsListResponse>(`/api/v1/models${params}`);
  }

  // Model tier info endpoint (for tier badge display and marketing)
  async getModelTierInfo(): Promise<ModelTierInfoResponse> {
    return this.request<ModelTierInfoResponse>('/api/v1/models/tier-info');
  }

  // Feedback endpoints
  async submitFeedback(data: FeedbackRequest): Promise<Feedback> {
    return this.request<Feedback>('/api/v1/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFeedbackHistory(limit = 10): Promise<Feedback[]> {
    const response = await this.request<{ feedback: Feedback[]; total: number }>(`/api/v1/feedback?limit=${limit}`);
    return response.feedback;
  }

  async getMyFeedback(limit = 10): Promise<{ feedback: Feedback[]; total: number }> {
    return this.request(`/api/v1/feedback?limit=${limit}`);
  }

  // Contact form endpoint (public, no auth required)
  async submitContactForm(data: ContactFormRequest): Promise<ContactFormResponse> {
    return this.request<ContactFormResponse>('/api/v1/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Stripe checkout endpoints
  async getStripeConfig(): Promise<{ publishable_key: string }> {
    return this.request('/api/v1/checkout/config');
  }

  async createCheckoutSession(priceId: string): Promise<{ checkout_url: string; session_id: string }> {
    return this.request('/api/v1/checkout/create-session', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId }),
    });
  }

  async createTrialCheckoutSession(priceId: string): Promise<{ checkout_url: string; session_id: string; trial_days: number; tier: string }> {
    return this.request('/api/v1/checkout/trial', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId }),
    });
  }

  async createEmbeddedCheckoutSession(priceId: string, isTrial: boolean = false): Promise<{ client_secret: string; session_id: string }> {
    return this.request('/api/v1/checkout/embedded', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId, is_trial: isTrial }),
    });
  }

  async createBillingPortalSession(): Promise<{ portal_url: string }> {
    return this.request('/api/v1/billing/portal', {
      method: 'POST',
    });
  }

  // Admin monitoring endpoints
  async getAdminErrors(params?: {
    hours?: number;
    severity?: ErrorSeverity;
    category?: ErrorCategory;
    limit?: number;
  }): Promise<AdminErrorsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.hours) searchParams.append('hours', params.hours.toString());
    if (params?.severity) searchParams.append('severity', params.severity);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const query = searchParams.toString();
    return this.request(`/api/v1/admin/errors${query ? `?${query}` : ''}`);
  }

  async getAdminErrorDetail(errorId: string): Promise<AdminErrorDetail> {
    return this.request(`/api/v1/admin/errors/${errorId}`);
  }

  async getAdminErrorSummary(hours?: number): Promise<AdminErrorSummary> {
    const query = hours ? `?hours=${hours}` : '';
    return this.request(`/api/v1/admin/errors/summary${query}`);
  }

  async resolveAdminError(errorId: string, notes?: string): Promise<{ status: string; error_id: string }> {
    return this.request(`/api/v1/admin/errors/${errorId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // API Usage Monitoring endpoints
  async getApiUsageSummary(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{
    summary: {
      total_calls: number;
      total_input_tokens: number;
      total_output_tokens: number;
      total_tokens: number;
      total_cost_usd: number;
      avg_latency_ms: number;
    };
    daily_data: Array<{
      date: string;
      calls: number;
      cost_usd: number;
    }>;
  }> {
    const query = new URLSearchParams();
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/v1/admin/usage/summary${queryStr}`);
  }

  async getApiUsageByProvider(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<Array<{
    provider: string;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    total_cost_usd: number;
  }>> {
    const query = new URLSearchParams();
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/v1/admin/usage/by-provider${queryStr}`);
  }

  async getApiUsageByModel(params?: {
    start_date?: string;
    end_date?: string;
    limit?: number;
  }): Promise<Array<{
    model: string;
    provider: string;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    total_cost_usd: number;
  }>> {
    const query = new URLSearchParams();
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    if (params?.limit) query.append('limit', params.limit.toString());
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/v1/admin/usage/by-model${queryStr}`);
  }

  async getApiUsageByUser(params?: {
    start_date?: string;
    end_date?: string;
    limit?: number;
  }): Promise<Array<{
    customer_email: string;
    customer_id: string;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    total_cost_usd: number;
  }>> {
    const query = new URLSearchParams();
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    if (params?.limit) query.append('limit', params.limit.toString());
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/v1/admin/usage/by-user${queryStr}`);
  }

  async getApiUsageLogs(params?: {
    start_date?: string;
    end_date?: string;
    provider?: string;
    customer_email?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    logs: Array<{
      id: number;
      timestamp: string;
      customer_email: string;
      model: string;
      provider: string;
      input_tokens: number;
      output_tokens: number;
      total_cost_usd: number;
      prompt_preview: string;
      response_preview: string;
      latency_ms: number;
      status: string;
    }>;
    total_count: number;
    limit: number;
    offset: number;
  }> {
    const query = new URLSearchParams();
    if (params?.start_date) query.append('start_date', params.start_date);
    if (params?.end_date) query.append('end_date', params.end_date);
    if (params?.provider) query.append('provider', params.provider);
    if (params?.customer_email) query.append('customer_email', params.customer_email);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    const queryStr = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/api/v1/admin/usage/logs${queryStr}`);
  }

  // Admin active generations monitoring
  async getActiveGenerations(): Promise<ActiveGenerationsResponse> {
    return this.request('/api/v1/admin/active-generations');
  }

  // Admin enrichment monitoring
  async getEnrichmentStats(): Promise<any> {
    return this.request('/api/v1/admin/enrichment/stats');
  }

  async getEnrichmentEvents(limit?: number): Promise<any> {
    const query = limit ? `?limit=${limit}` : '';
    return this.request(`/api/v1/admin/enrichment/events${query}`);
  }

  async getEnrichmentBreakdown(): Promise<any> {
    return this.request('/api/v1/admin/enrichment/breakdown');
  }

  async getEnrichmentFalsePositives(): Promise<any> {
    return this.request('/api/v1/admin/enrichment/false-positives');
  }

  async getEnrichmentFalseNegatives(): Promise<any> {
    return this.request('/api/v1/admin/enrichment/false-negatives');
  }

  // Admin user spending stats
  async getAdminUserSpending(): Promise<UserSpendingResponse> {
    return this.request('/api/v1/admin/user-spending');
  }

  // Admin recent generations history with pagination and filters
  async getAdminRecentGenerations(params?: {
    limit?: number;
    offset?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
  }): Promise<RecentGenerationsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    return this.request(`/api/v1/admin/recent-generations${query ? `?${query}` : ''}`);
  }

  // Admin generation detail
  async getAdminGenerationDetail(historyId: string): Promise<GenerationDetail> {
    return this.request(`/api/v1/admin/generation/${historyId}`);
  }

  // Admin analytics
  async getAdminAnalytics(days: number = 30): Promise<AnalyticsResponse> {
    return this.request(`/api/v1/admin/analytics?days=${days}`);
  }

  // Admin compare generations
  async compareGenerations(historyIds: string[]): Promise<ComparisonResponse> {
    return this.request(`/api/v1/admin/compare-generations?history_ids=${historyIds.join(',')}`);
  }

  // Admin cache management
  async getAdminCacheStats(): Promise<CacheStatsResponse> {
    return this.request('/api/v1/admin/cache/stats');
  }

  async cleanupAdminCache(): Promise<CacheCleanupResponse> {
    return this.request('/api/v1/admin/cache/cleanup', { method: 'POST' });
  }

  // Admin free tier reports
  async getAdminFreeReports(limit: number = 100): Promise<FreeReportsResponse> {
    return this.request(`/api/v1/admin/free-reports?limit=${limit}`);
  }

  // ==========================================================================
  // Verdict Admin Monitoring
  // ==========================================================================

  /** Get Verdict admin statistics with date range filtering */
  async getVerdictAdminStats(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<VerdictAdminStatsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const query = queryParams.toString();
    return this.request(`/api/v1/admin/verdict/stats${query ? `?${query}` : ''}`);
  }

  /** Get currently active logic trees (research workflows) */
  async getVerdictActiveLogicTrees(): Promise<VerdictActiveLogicTreesResponse> {
    return this.request('/api/v1/admin/verdict/active-logic-trees');
  }

  /** Get fingerprint tracking records */
  async getVerdictFingerprints(params?: {
    limit?: number;
    offset?: number;
    start_date?: string;
    end_date?: string;
    converted_only?: boolean;
  }): Promise<VerdictFingerprintsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.converted_only) queryParams.append('converted_only', 'true');
    const query = queryParams.toString();
    return this.request(`/api/v1/admin/verdict/fingerprints${query ? `?${query}` : ''}`);
  }

  /** Get research sessions across all users */
  async getVerdictResearchSessions(params?: {
    limit?: number;
    offset?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
  }): Promise<VerdictResearchSessionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString();
    return this.request(`/api/v1/admin/verdict/research-sessions${query ? `?${query}` : ''}`);
  }

  /** Get cost tracking stats for admin monitoring */
  async getVerdictCostTracking(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<VerdictCostTrackingResponse> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    const query = queryParams.toString();
    return this.request(`/api/v1/admin/verdict/cost-tracking${query ? `?${query}` : ''}`);
  }

  // Admin influencer management
  async createInfluencerAccount(data: CreateInfluencerRequest): Promise<CreateInfluencerResponse> {
    return this.request('/api/v1/admin/create-influencer', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInfluencerAccounts(): Promise<InfluencerAccountsResponse> {
    return this.request('/api/v1/admin/influencers');
  }

  async deleteInfluencerAccount(customerId: string): Promise<{ status: string; customer_id: string; email: string }> {
    return this.request(`/api/v1/admin/influencers/${customerId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get the SSE URL for streaming progress updates
   * Note: This returns the full URL for SSE connection
   */
  getProgressStreamUrl(historyId: string): string {
    return `${API_BASE}/api/v1/newsletter/progress/${historyId}`;
  }

  /**
   * Get the auth token for SSE connection
   */
  getAuthToken(): string | null {
    return this.getToken();
  }

  // ==========================================================================
  // Research History API (pgvector-based semantic search)
  // ==========================================================================

  /**
   * Get user's research history with vector-indexed insights
   */
  async getResearchHistory(limit: number = 20, offset: number = 0): Promise<ResearchHistoryResponse> {
    return this.request(`/api/v1/research/history?limit=${limit}&offset=${offset}`);
  }

  /**
   * Find similar past research using semantic vector search
   */
  async findSimilarResearch(
    query: string,
    limit: number = 5,
    minSimilarity: number = 0.5
  ): Promise<SimilarResearchResponse> {
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
      min_similarity: minSimilarity.toString(),
    });
    return this.request(`/api/v1/research/similar?${params}`);
  }

  /**
   * Get research statistics for the current user
   */
  async getResearchStats(): Promise<ResearchStats> {
    return this.request('/api/v1/research/stats');
  }

  /**
   * Check if a topic is novel or has been recently researched
   */
  async checkTopicNovelty(topic: string, daysLookback: number = 7): Promise<TopicNoveltyResponse> {
    const params = new URLSearchParams({
      topic,
      days_lookback: daysLookback.toString(),
    });
    return this.request(`/api/v1/research/check-novelty?${params}`);
  }

  // =========================================================================
  // Free Tier Endpoints (public, no auth required)
  // =========================================================================

  /**
   * Request a free report (lead magnet)
   */
  async requestFreeReport(data: FreeReportRequest): Promise<FreeReportResponse> {
    return this.request<FreeReportResponse>('/api/v1/free-report', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Check free tier status for an email
   */
  async checkFreeReportStatus(email: string): Promise<FreeReportStatusResponse> {
    return this.request(`/api/v1/free/status?email=${encodeURIComponent(email)}`);
  }

  /**
   * Check fingerprint query status for Sealed Verdict flow
   */
  async checkFingerprintStatus(fingerprintId: string): Promise<FingerprintCheckResponse> {
    return this.request<FingerprintCheckResponse>('/api/v1/fingerprint/check', {
      method: 'POST',
      body: JSON.stringify({ fingerprint_id: fingerprintId } as FingerprintCheckRequest),
    });
  }

  // ==========================================================================
  // Research Sessions API (Verdict Dashboard)
  // ==========================================================================

  /**
   * Get user's research sessions
   */
  async getResearchSessions(options?: {
    status?: 'active' | 'archived';
    session_type?: 'general' | 'financial' | 'strategic';
  }): Promise<{
    sessions: Array<{
      id: string;
      customer_id: string;
      title: string | null;
      status: string;
      session_type: string;
      created_at: string;
      updated_at: string;
      last_accessed_at: string;
      message_count: number;
    }>;
    total: number;
    has_more: boolean;
  }> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.session_type) params.set('session_type', options.session_type);
    const qs = params.toString();
    return this.request(`/api/v1/research/sessions${qs ? `?${qs}` : ''}`);
  }

  /**
   * Get a specific research session with messages
   */
  async getResearchSession(sessionId: string): Promise<{
    id: string;
    customer_id: string;
    title: string | null;
    status: string;
    session_type: string;
    created_at: string;
    updated_at: string;
    last_accessed_at: string;
    messages: Array<{
      id: string;
      session_id: string;
      role: string;
      content: string;
      message_type: string;
      created_at: string;
      research_history_id: number | null;
    }>;
  }> {
    return this.request(`/api/v1/research/sessions/${sessionId}`);
  }

  /**
   * Create a new research session
   */
  async createResearchSession(data: {
    title?: string | null;
    initial_query?: string | null;
    session_type?: 'general' | 'financial' | 'strategic';
  }): Promise<{
    id: string;
    customer_id: string;
    title: string | null;
    status: string;
    session_type: string;
    created_at: string;
    updated_at: string;
    last_accessed_at: string;
    messages: Array<{
      id: string;
      session_id: string;
      role: string;
      content: string;
      message_type: string;
      created_at: string;
      research_history_id: number | null;
    }>;
  }> {
    return this.request('/api/v1/research/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Add a message to a research session
   */
  async addResearchSessionMessage(
    sessionId: string,
    data: { content: string; message_type: string; role: string }
  ): Promise<{
    id: string;
    session_id: string;
    role: string;
    content: string;
    message_type: string;
    created_at: string;
    research_history_id: number | null;
  }> {
    return this.request(`/api/v1/research/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Archive a research session (soft delete)
   */
  async archiveResearchSession(sessionId: string): Promise<{ status: string }> {
    return this.request(`/api/v1/research/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get available models for quick chat
   */
  async getQuickChatModels(): Promise<{
    models: Array<{
      id: string;
      name: string;
      provider: string;
    }>;
  }> {
    return this.request('/api/v1/research/chat/models', {
      method: 'GET',
    });
  }

  /**
   * Quick chat response using existing context (no full research)
   */
  async quickChat(
    sessionId: string,
    question: string,
    options?: {
      useSessionContext?: boolean;
      useGlobalContext?: boolean;
      model?: string;  // Specific model to use (e.g., "openai/gpt-4o")
    }
  ): Promise<{
    answer: string;
    sources_used: string[];
    confidence: 'high' | 'medium' | 'low';
    suggest_deep_research: boolean;
    message_id: string;
    model_used: string;
  }> {
    return this.request('/api/v1/research/chat/quick', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        question: question,
        use_session_context: options?.useSessionContext ?? true,
        use_global_context: options?.useGlobalContext ?? true,
        model: options?.model,
      }),
    });
  }

  /**
   * Stream quick chat response via SSE (token-by-token).
   * Returns an AbortController that the caller can use to cancel the stream.
   */
  quickChatStream(
    sessionId: string,
    question: string,
    options: {
      useSessionContext?: boolean;
      useGlobalContext?: boolean;
      model?: string;
    } | undefined,
    callbacks: {
      onToken: (content: string) => void;
      onMetadata: (data: { model_used: string; confidence: string; suggest_deep_research: boolean; sources_used: string[] }) => void;
      onMessageStored: (data: { message_id: string }) => void;
      onDone: () => void;
      onError: (error: string) => void;
    }
  ): AbortController {
    const controller = new AbortController();
    const token = localStorage.getItem('access_token');

    const run = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/research/chat/quick-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            session_id: sessionId,
            question: question,
            use_session_context: options?.useSessionContext ?? true,
            use_global_context: options?.useGlobalContext ?? true,
            model: options?.model,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          callbacks.onError(errorText || `HTTP ${response.status}`);
          return;
        }

        if (!response.body) {
          callbacks.onError('No response body');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            } else if (line === '' && eventType && eventData) {
              try {
                const parsed = JSON.parse(eventData);
                switch (eventType) {
                  case 'token':
                    callbacks.onToken(parsed.content);
                    break;
                  case 'metadata':
                    callbacks.onMetadata(parsed);
                    break;
                  case 'message_stored':
                    callbacks.onMessageStored(parsed);
                    break;
                  case 'done':
                    callbacks.onDone();
                    break;
                  case 'error':
                    callbacks.onError(parsed.error);
                    break;
                }
              } catch {
                // Skip malformed JSON
              }
              eventType = '';
              eventData = '';
            }
          }
        }

        // If stream ends without done event, call onDone
        callbacks.onDone();
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          callbacks.onError((e as Error).message || 'Stream failed');
        }
      }
    };

    run();
    return controller;
  }

  /**
   * Detect the optimal research mode for a query using backend LLM classifier.
   * Lightweight call (~200-500ms) that resolves 'auto' to the best mode.
   */
  async detectResearchMode(
    topic: string,
    mode: 'auto' | 'general' | 'financial' | 'strategic' = 'auto'
  ): Promise<{
    effective_mode: string;
    confidence: number;
    signals: string[];
    reasoning: string;
  }> {
    return this.request('/api/v1/research/detect-mode', {
      method: 'POST',
      body: JSON.stringify({ topic, mode }),
    });
  }

  /**
   * Plan streaming research for authenticated users
   * Returns session_id, task breakdown, and smart routing info for UI display
   */
  async planStreamResearch(
    topic: string,
    sessionId?: string,
    intensity: 'fast' | 'deep' = 'fast',
    mode: 'auto' | 'general' | 'financial' | 'strategic' = 'auto'
  ): Promise<{
    session_id: string;
    tasks: Array<{
      name: string;
      description: string;
      execution_mode: string;
      required_capability?: string;
    }>;
    planning_time_ms: number;
    intensity: string;
    credit_multiplier: string;
    mode: string;
  }> {
    return this.request('/api/v1/research/stream/plan', {
      method: 'POST',
      body: JSON.stringify({
        topic,
        session_id: sessionId,
        intensity,
        mode,
      }),
    });
  }

  /**
   * Get the SSE streaming URL for authenticated research execution
   */
  getStreamExecuteUrl(): string {
    return `${API_BASE}/api/v1/research/stream/execute`;
  }

  // ============================================================================
  // Credit System Endpoints
  // ============================================================================

  /** Get user's credit balance */
  async getCreditsBalance(): Promise<{
    credit_balance: number;
    is_unlimited: boolean;
    is_paid_user: boolean;
  }> {
    return this.request('/api/v1/credits/balance');
  }

  /** Get available credit packs */
  async getCreditPacks(): Promise<{
    packs: Array<{
      id: string;
      name: string;
      description: string;
      price_usd: number;
      credits: number;
      bonus_percent: number;
    }>;
  }> {
    return this.request('/api/v1/credits/packs');
  }

  /** Estimate cost for a query type */
  async estimateCreditCost(
    queryType: string,
    options?: { model_preferences?: string[] }
  ): Promise<{
    estimated_credits: number;
    max_cost: number;
    query_type: string;
    model_info: Record<string, unknown>;
  }> {
    return this.request('/api/v1/credits/estimate', {
      method: 'POST',
      body: JSON.stringify({
        query_type: queryType,
        model_preferences: options?.model_preferences,
      }),
    });
  }

  /** Purchase a credit pack - returns Stripe checkout URL */
  async purchaseCreditPack(packId: string): Promise<{
    checkout_url: string;
  }> {
    return this.request(`/api/v1/credits/purchase?pack_id=${encodeURIComponent(packId)}`, {
      method: 'POST',
    });
  }

  /** Check status of a specific credit purchase */
  async getCreditPurchaseStatus(purchaseId: string): Promise<{
    purchase_id: string;
    status: string;
    credits_purchased: number;
  }> {
    return this.request(`/api/v1/credits/purchases/${encodeURIComponent(purchaseId)}/status`);
  }

  /** Get credit transaction history */
  async getCreditHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    transactions: Array<{
      id: string;
      amount: number;
      balance_after: number;
      transaction_type: string;
      description: string;
      created_at: string;
    }>;
    total: number;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryString = query.toString();
    return this.request(`/api/v1/credits/history${queryString ? `?${queryString}` : ''}`);
  }

  /** Get credit pack purchase history with receipt URLs */
  async getPurchaseHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    purchases: Array<{
      id: string;
      pack_type: string;
      pack_name: string;
      credits_purchased: number;
      amount_paid: number;
      currency: string;
      status: 'pending' | 'completed' | 'failed';
      created_at: string | null;
      completed_at: string | null;
      receipt_url: string | null;
    }>;
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryString = query.toString();
    return this.request(`/api/v1/credits/purchases${queryString ? `?${queryString}` : ''}`);
  }

  // =======================================================================
  // Research Runs API (user-facing billing)
  // =======================================================================

  /** Get current run usage for the authenticated user */
  async getRunUsage(): Promise<{
    runs_used: number;
    runs_limit: number;
    bonus_runs: number;
    subscription_remaining: number;
    total_available: number;
    period_end: string | null;
  }> {
    return this.request('/api/v1/runs/usage');
  }

  /** Get available run packs for purchase */
  async getRunPacks(): Promise<{
    packs: Array<{
      id: string;
      name: string;
      runs: number;
      price_usd: number;
      description: string;
    }>;
  }> {
    return this.request('/api/v1/runs/packs');
  }

  /** Purchase a run pack - returns Stripe checkout URL */
  async purchaseRunPack(packId: string): Promise<{
    checkout_url: string;
  }> {
    return this.request(`/api/v1/runs/purchase?pack_id=${encodeURIComponent(packId)}`, {
      method: 'POST',
    });
  }

  // =======================================================================
  // Financial Deep Dive API
  // =======================================================================

  /**
   * Run Financial Deep Dive analysis on a stock ticker.
   * Uses Bull vs Bear agent debate with Kill Sheet output.
   */
  async analyzeFinancial(params: {
    ticker: string;
    depth?: 'fast' | 'deep';
    question?: string;
  }): Promise<{
    success: boolean;
    ticker: string;
    kill_sheet: {
      ticker: string;
      analysis_date: string;
      winning_side: string;
      confidence: number;
      bull_thesis: string;
      bear_thesis: string;
      decisive_factor: string;
      rejected_arguments: Array<{
        side: string;
        agent_role: string;
        agent_model: string;
        argument_summary: string;
        rejection_reason: string;
        rejection_detail: string;
        counter_evidence?: string;
        sources_cited: string[];
      }>;
      caveats: string;
      data_sources: Record<string, string>;
      research_depth: string;
      total_agents: number;
      debate_rounds: number;
    };
    summary: string;
  }> {
    return this.request('/api/v1/financial/analyze', {
      method: 'POST',
      body: JSON.stringify({
        ticker: params.ticker,
        depth: params.depth || 'fast',
        question: params.question,
      }),
    });
  }

  /**
   * Run Financial Deep Dive analysis with SSE streaming.
   * Returns an EventSource that emits progress events.
   *
   * Event types:
   * - start: Analysis started
   * - data_fetch_start/complete: FMP and SEC data fetching
   * - bull_team_start/complete: Bull agent team progress
   * - bear_team_start/complete: Bear agent team progress
   * - agent_start/complete: Individual agent progress
   * - debate_start/complete: Adversarial debate progress
   * - done: Complete Kill Sheet result
   * - error: Error occurred
   */
  analyzeFinancialStream(params: {
    ticker: string;
    depth?: 'fast' | 'deep';
    question?: string;
  }): {
    eventSource: EventSource | null;
    cancel: () => void;
    subscribe: (onEvent: (eventType: string, data: unknown) => void, onError: (error: Error) => void) => void;
  } {
    const token = this.getToken();
    if (!token) {
      return {
        eventSource: null,
        cancel: () => {},
        subscribe: (_onEvent: (eventType: string, data: unknown) => void, onError: (error: Error) => void) => {
          onError(new Error('Not authenticated'));
        }
      };
    }

    // For streaming, we need to make a POST request that returns SSE
    // Unfortunately EventSource only supports GET, so we use fetch with ReadableStream
    const controller = new AbortController();

    const fetchStream = async (
      onEvent: (eventType: string, data: unknown) => void,
      onError: (error: Error) => void
    ) => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/financial/analyze/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ticker: params.ticker,
            depth: params.depth || 'fast',
            question: params.question,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEventType = '';
          let currentData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEventType = line.slice(7);
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6);
            } else if (line === '' && currentEventType && currentData) {
              try {
                const parsedData = JSON.parse(currentData);
                onEvent(currentEventType, parsedData);
              } catch {
                onEvent(currentEventType, currentData);
              }
              currentEventType = '';
              currentData = '';
            }
          }
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          onError(error as Error);
        }
      }
    };

    return {
      eventSource: null, // Not using EventSource for POST
      cancel: () => controller.abort(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subscribe: (onEvent: (eventType: string, data: any) => void, onError: (error: Error) => void) => {
        fetchStream(onEvent, onError);
      },
    } as {
      eventSource: EventSource | null;
      cancel: () => void;
      subscribe: (onEvent: (eventType: string, data: unknown) => void, onError: (error: Error) => void) => void;
    };
  }

  // Twitter Admin endpoints
  async getTwitterDashboard(): Promise<import('@/types').TwitterDashboardStats> {
    return this.request('/api/v1/twitter/dashboard');
  }

  async getTwitterPosts(limit = 50, offset = 0): Promise<import('@/types').TwitterPost[]> {
    return this.request(`/api/v1/twitter/posts?limit=${limit}&offset=${offset}`);
  }

  async getTwitterTopics(limit = 20): Promise<import('@/types').TwitterTopic[]> {
    return this.request(`/api/v1/twitter/topics?limit=${limit}`);
  }

  async postTweetNow(text: string, thread = false): Promise<{ post_id: string; task_id: string; tweets: string[] }> {
    return this.request('/api/v1/twitter/post-now', {
      method: 'POST',
      body: JSON.stringify({ text, thread }),
    });
  }

  async triggerTwitterDiscovery(): Promise<{ task_id: string; status: string }> {
    return this.request('/api/v1/twitter/discover-now', { method: 'POST' });
  }

  async triggerTwitterMetricsUpdate(): Promise<{ task_id: string; status: string }> {
    return this.request('/api/v1/twitter/update-metrics', { method: 'POST' });
  }

  async deleteTwitterPost(postId: string): Promise<{ status: string; post_id: string }> {
    return this.request(`/api/v1/twitter/posts/${postId}`, { method: 'DELETE' });
  }

  async getTwitterABTests(testName: string): Promise<import('@/types').TwitterABTestResult[]> {
    return this.request(`/api/v1/twitter/ab-tests/${testName}`);
  }

  // Portfolio chart data
  async getPortfolioChart(days: number = 365): Promise<{
    data_points: Array<{ date: string; value: number }>;
    period_return_pct: number;
    has_real_data: boolean;
  }> {
    return this.request(`/api/v1/portfolio/chart?days=${days}`);
  }
}

export const api = new ApiClient();

/** API base URL for SSE connections */
export const API_BASE_URL = API_BASE;

